import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import storage from "../../../utils/storage";
import { useDispatch, useSelector } from "react-redux";
import appActions from "../../../appActions";
import PrimeTable from "../../../components/PrimeTable";
import SiteGroupForm from "./SiteGroupForm";
import SiteGroupFilterBar from "./SiteGroupFilterBar";
import SiteGroupHeader from "./SiteGroupHeader";
import Sidebar from "../../../components/Sidebar";
import { Toast } from "primereact/toast";

const SiteGroupList = () => {

    const dispatch = useDispatch();

    const toast = useRef(null);

    const userInfo = useMemo(() => JSON.parse(storage.get('loginUser')), []);
  //  const { userRole, userId, siteId } = userInfo;

    const { siteGroupList, siteGroupDetails } = useSelector((state) => state?.sitegroup);

    const syncedState = useSelector((state) => state?.sitegroup?.siteGroupSynced);

    const errorCode = syncedState?.message;
    const synced = syncedState?.synced;
    const status = syncedState?.status;

    useEffect(() => {
        if (status === "failed") {
          toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: errorCode,
                life: 3000,
                onHide: () => {
                    dispatch(appActions.SITEGROUP_SYNCGROUP_RESET());
                }
            });
        } else if (status === "success") {
           toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: errorCode,
                life: 3000,
                onHide: () => {
                    console.log('Toast closed');
                    // example: reset state after toast closes
                    dispatch(appActions.SITEGROUP_SYNCGROUP_RESET());
                }
            });
        }
    }, [errorCode, synced, status, dispatch]); 

    const defaultParams = useMemo(() => ({
        pageno: 1,
        pagesize: 10,
        sort: 'createdOn',
        sortOrder : -1,
    }), []);

    const [formValues, setFormValues] = useState(defaultParams);
    const [filterParams, setFilterParams] = useState({});

    // New states for dialog control and selected user
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [selectedSiteGroup, setSelectedSiteGroup] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [isCopyMode, setIsCopyMode] = useState(false);

    const fetchSiteGroup = useCallback((params = {}) => {
        dispatch(appActions.SITEGROUP_GET_ALL_REQUEST({ ...defaultParams, ...params }));
    }, [dispatch, defaultParams]);

    const handleFilterTask = useCallback((params) => {
        setFilterParams(params);
        fetchSiteGroup({...defaultParams, ...params});
    }, [fetchSiteGroup, defaultParams]);

    useEffect(() => {
        fetchSiteGroup();
    }, [dispatch]);

    const handleCopyRedirect = useCallback((record) => {
        const data = siteGroupList?.data?.find((t) => t.id === record.id);
        setSelectedSiteGroup(data);
        setIsViewMode(false);
        setIsEditMode(false);
        setIsCopyMode(true);
        setIsDialogVisible(true); // Open dialog in copy mode
    }, [siteGroupList]);

    const handleViewRedirect = useCallback((record) => {
        const data = siteGroupList?.data?.find((t) => t.id === record.id);
        setSelectedSiteGroup(data);
        setIsViewMode(true);
        setIsEditMode(false);
        setIsDialogVisible(true);
    }, [siteGroupList]);

    const handleEditRedirect = useCallback((record) => {
        const data = siteGroupList?.data?.find((t) => t.id === record.id);
        setSelectedSiteGroup(data);
        setIsEditMode(true);
        setIsViewMode(false);
        setIsDialogVisible(true);
    }, [siteGroupList]);

    const handleDelete = useCallback((record) => {
        const { id } = record;
        dispatch(appActions.SITEGROUP_DELETE_REQUEST({ id }));
    }, [dispatch]);

    const handleDialogClose = () => {
        setIsDialogVisible(false);
        setSelectedSiteGroup(null);
    };

    const handleDialogSave = () => {
        fetchSiteGroup();
        setIsDialogVisible(false);
    }

    const handleSearch = useCallback(
        (params) => {

            const searchParams = {
                ...params,
                pagesize: params.pagesize || defaultParams?.pagesize,
                pageno: params.pageno || defaultParams?.pageno,
                sort: params.sort || defaultParams?.sort,
                sortOrder: params.sortOrder || defaultParams?.sortOrder,
            };

            dispatch(appActions.SITEGROUP_GET_ALL_REQUEST(searchParams));
        },
        [dispatch],
    );

    const onFilterChangeCallback  = useCallback(
        (event) => {

            const { sortField, sortOrder, rows, page } = event;
            
            const params = {...formValues, pagesize: rows, pageno: page, sort: sortField, sortOrder: sortOrder };

            setFormValues(prevFormValues => ({
                ...prevFormValues,
                sort: sortField, 
                sortOrder: sortOrder,
                pagesize: rows,
                pageno: page
            }));

            handleSearch(params); 
        },

        [handleSearch, formValues],
    );

    
    useEffect(() => {
        if (siteGroupDetails?.action === 'delete' || siteGroupDetails?.action === 'create' || siteGroupDetails?.action === 'update' || synced === true ) {
            fetchSiteGroup();
        }
    }, [siteGroupDetails, fetchSiteGroup, synced]);

    const columns = [
        // { field: 'groupId', header: 'Site Group Id', sortable: true},
        { field: 'groupCode', header: 'Site Group Code', sortable: true},
        { field: 'groupName', header: 'Site Group Name', sortable: true},
        { field: 'status', header: 'Status', sortable: true},
    ];

    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                <span className="cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                    <i className="pi pi-eye text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleEditRedirect(rowData)} data-pr-tooltip="Edit">
                    <i className="pi pi-pencil text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleCopyRedirect(rowData)} data-pr-tooltip="Copy">
                    <i className="pi pi-copy text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                    <i className="pi pi-times-circle text-base text-danger"/>
                </span>
            </div>
        ),
        [handleCopyRedirect, handleViewRedirect, handleEditRedirect, handleDelete],
    );

    return (
        <div className="w-full">
            <Toast ref={toast} onHide={() => {
                dispatch(appActions.SITEGROUP_SYNCGROUP_RESET_REQUEST());
            }}/>
            <SiteGroupHeader />
            <div className="flex flex-min-height">
                <Sidebar />
                <div className="p-grid w-full pl-60px">
                    <h3 className="title">Site Group Management</h3>
                    <SiteGroupFilterBar
                        handleFilter={handleFilterTask}
                        filterParams={filterParams}
                        userInfo={userInfo}
                        handleSearch={handleSearch}
                    />
                    <PrimeTable
                        list={siteGroupList}
                        columns={columns}
                        actionColumn={actionTemplate}
                        tableName='site_group_management'
                        isLazy={true}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        isExport={false}
                    />
                </div>

                {/* Dialog for User Form */}
                <Dialog
                    visible={isDialogVisible}
                    header={isEditMode ? "Edit Site" : isCopyMode ? "Copy Site" : "View Site"}
                    onHide={handleDialogClose}
                    style={{ width: '50vw' }}
                >
                    <SiteGroupForm
                        siteGroup={selectedSiteGroup}
                        isEditMode={isEditMode}
                        isViewMode={isViewMode}
                        isCopyMode={isCopyMode}
                        onClose={handleDialogClose}
                        onSave={handleDialogSave}
                    />
                </Dialog>
            </div>
        </div>
    );
}

export default SiteGroupList;
